import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatFormField, MatInput, MatLabel } from '@angular/material/input'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { addDays, differenceInDays } from 'date-fns'
import { DatetimePickerComponent } from '../../common/datetime-picker/datetime-picker.component'
import { DATE_TIME_FORMAT, resolveLocalTimeZone } from '../../utils'
import { formatDayCount } from './retention-day-count'
import { RetentionTimelineComponent, RetentionTimelineView } from './retention-timeline/retention-timeline.component'

const QUICK_EXTENSIONS_IN_DAYS = [1, 2, 7]

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

/**
 * Rounded rather than truncated: the two deletion dates are a whole number of days apart in absolute
 * terms, but a daylight-saving change between them leaves them an hour short of that in local time,
 * which truncation would report as a day less.
 */
function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / MILLISECONDS_IN_DAY)
}

/**
 * How far past the current deletion date the scale reaches for an organization that caps nothing:
 * there is no maximum to end it at, and a month of room is enough to see the markers move.
 */
const UNCAPPED_LOOKAHEAD_IN_DAYS = 30

/** How much of the scale is left empty past its last date, so a marker never sits on the edge. */
const SCALE_HEADROOM = 0.05

export type ExtendRetentionDialogData = {
  callName: string
  /** When the meeting took place, where the timeline starts. */
  meetingDate: Date
  /** When the meeting is hidden from its organization as things stand. */
  currentSoftDeletionDate: Date
  /** When it is deleted for good as things stand. */
  currentDeletionDate: Date
  /** Where that date stood before any extension moved it, which the total cap measures from. */
  originalDeletionDate: Date
  /** The furthest the date may be pushed, or null when the organization sets no cap. */
  maximumDeletionDate: Date | null
  maximumDeletionDateBySingleExtension: Date | null
  maximumDeletionDateByTotalExtension: Date | null
}

export type ExtendRetentionDialogResult = {
  deletionDate: Date
}

/** Which of the organization's two caps rules a quick extension out; each is explained in its own words. */
type BlockingLimit = 'SINGLE' | 'TOTAL'

@Component({
  selector: 'app-extend-retention-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatFormField,
    MatLabel,
    MatInput,
    TranslatePipe,
    TranslateDirective,
    DatetimePickerComponent,
    RetentionTimelineComponent,
    DatePipe,
  ],
  providers: [DatePipe],
  templateUrl: './extend-retention-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './extend-retention-dialog.component.scss',
})
export class ExtendRetentionDialogComponent {
  data: ExtendRetentionDialogData = inject<ExtendRetentionDialogData>(MAT_DIALOG_DATA)
  private translateService = inject(TranslateService)
  private datePipe = inject(DatePipe)

  // The picker reads and writes a plain Date, so what the user types is in their own time zone. Every
  // date here is shown in it, and it is named, rather than being quietly converted to London's.
  localTimeZone = resolveLocalTimeZone()
  dateTimeFormat = DATE_TIME_FORMAT

  readonly quickExtensionsInDays = QUICK_EXTENSIONS_IN_DAYS

  // The dates reach the dialog as they came off the wire, so each is read into a Date of its own
  // before any arithmetic is done on it.
  private meetingDate = new Date(this.data.meetingDate)
  private currentSoftDeletionDate = new Date(this.data.currentSoftDeletionDate)
  private currentDeletionDate = new Date(this.data.currentDeletionDate)
  private originalDeletionDate = new Date(this.data.originalDeletionDate)
  private maximumDeletionDate: Date | null = this.data.maximumDeletionDate === null ? null : new Date(this.data.maximumDeletionDate)
  private maximumBySingleExtension: Date | null =
    this.data.maximumDeletionDateBySingleExtension === null ? null : new Date(this.data.maximumDeletionDateBySingleExtension)
  private maximumByTotalExtension: Date | null =
    this.data.maximumDeletionDateByTotalExtension === null ? null : new Date(this.data.maximumDeletionDateByTotalExtension)

  /**
   * The stretch between the two deletions, which is the organization's own setting rather than
   * anything this dialog can change: moving the soft deletion carries the hard one along with it.
   *
   * Held to the millisecond. Rounding it to whole days would move the hard deletion off the date the
   * meeting actually has, and by an hour is enough for the dialog to open on a date it then rejects.
   */
  private recoveryWindowInMilliseconds = this.currentDeletionDate.getTime() - this.currentSoftDeletionDate.getTime()

  // Opens on the date the meeting stands at now, so the timeline reads as unchanged and Save waits
  // until an extension has actually been chosen.
  softDeletionDate: Date = new Date(this.currentSoftDeletionDate)

  private get recoveryWindowInDays(): number {
    return Math.round(this.recoveryWindowInMilliseconds / MILLISECONDS_IN_DAY)
  }

  /** The hard deletion that goes with a given soft deletion, the recovery window apart from it. */
  private deletionDateFor(softDeletionDate: Date): Date {
    return new Date(softDeletionDate.getTime() + this.recoveryWindowInMilliseconds)
  }

  /** The inverse: the soft deletion that puts the hard one on `deletionDate`. */
  private softDeletionDateFor(deletionDate: Date): Date {
    return new Date(deletionDate.getTime() - this.recoveryWindowInMilliseconds)
  }

  get deletionDate(): Date {
    return this.deletionDateFor(this.softDeletionDate)
  }

  get maximumSoftDeletionDate(): Date | null {
    return this.maximumDeletionDate === null ? null : this.softDeletionDateFor(this.maximumDeletionDate)
  }

  /**
   * Only pushing the date out is on offer here: pulling it in would destroy the recording earlier
   * than the organization agreed to, which is a support action rather than an administrator's.
   */
  get canSave(): boolean {
    return this.deletionDate > this.currentDeletionDate && !this.isBeyondMaximum
  }

  get isBeyondMaximum(): boolean {
    return this.maximumDeletionDate !== null && this.deletionDate > this.maximumDeletionDate
  }

  /** A date that has not been moved yet is not an error, so the field only complains once it is wrong. */
  get isDateInvalid(): boolean {
    return this.isBeyondMaximum || this.deletionDate < this.currentDeletionDate
  }

  get errorMessageKey(): string {
    return this.isBeyondMaximum ? 'CALLS.EXTEND_RETENTION.BEYOND_MAXIMUM' : 'CALLS.EXTEND_RETENTION.MUST_BE_LATER'
  }

  get recoveryWindowLabel(): string {
    return formatDayCount(this.translateService, this.recoveryWindowInDays)
  }

  quickExtensionLabel(days: number): string {
    return `+${formatDayCount(this.translateService, days)}`
  }

  /** Which cap a quick extension runs into, or null when it fits within both. */
  blockingLimitFor(days: number): BlockingLimit | null {
    const target = addDays(this.currentDeletionDate, days)
    const single = this.maximumBySingleExtension
    const total = this.maximumByTotalExtension
    const exceededSingle = single !== null && target > single ? single : null
    const exceededTotal = total !== null && target > total ? total : null
    if (exceededSingle === null) {
      return exceededTotal === null ? null : 'TOTAL'
    }
    if (exceededTotal === null) {
      return 'SINGLE'
    }
    // Both are in the way, so the one that bites first is the one worth naming.
    return exceededSingle <= exceededTotal ? 'SINGLE' : 'TOTAL'
  }

  isQuickExtensionBlocked(days: number): boolean {
    return this.blockingLimitFor(days) !== null
  }

  /** Empty for an extension that fits: there is then nothing to explain. */
  quickExtensionTooltip(days: number): string {
    const limit = this.blockingLimitFor(days)
    if (limit === null) {
      return ''
    }
    const limitDate = limit === 'SINGLE' ? this.maximumBySingleExtension : this.maximumByTotalExtension
    return this.translateService.instant(`CALLS.EXTEND_RETENTION.QUICK_EXTEND_BLOCKED_BY_${limit}`, {
      date: this.datePipe.transform(limitDate, DATE_TIME_FORMAT) ?? '',
    })
  }

  isQuickExtensionSelected(days: number): boolean {
    return this.deletionDate.getTime() === addDays(this.currentDeletionDate, days).getTime()
  }

  applyQuickExtension(days: number) {
    if (!this.isQuickExtensionBlocked(days)) {
      this.applyExtensionInDays(days)
    }
  }

  /** Nothing to jump to for an organization that caps nothing, so the option is not offered. */
  get hasMaximum(): boolean {
    return this.maximumDeletionDate !== null
  }

  get isMaximumSelected(): boolean {
    const maximum = this.maximumSoftDeletionDate
    return maximum !== null && this.softDeletionDate.getTime() === maximum.getTime()
  }

  applyMaximum() {
    const maximum = this.maximumSoftDeletionDate
    if (maximum !== null) {
      this.softDeletionDate = maximum
    }
  }

  applyExtensionInDays(days: number) {
    this.softDeletionDate = this.softDeletionDateFor(addDays(this.currentDeletionDate, days))
  }

  get timeline(): RetentionTimelineView {
    return {
      meetingDate: this.meetingDate,
      currentSoftDeletionDate: this.currentSoftDeletionDate,
      softDeletionDate: this.softDeletionDate,
      deletionDate: this.deletionDate,
      originalSoftDeletionDate: this.softDeletionDateFor(this.originalDeletionDate),
      originalDeletionDate: this.originalDeletionDate,
      maximumSoftDeletionDate: this.maximumSoftDeletionDate,
      maximumDeletionDate: this.maximumDeletionDate,
      windowEndDate: this.windowEndDate,
      recoveryWindowInDays: this.recoveryWindowInDays,
      extendedFromOriginalInDays: daysBetween(this.originalDeletionDate, this.deletionDate),
      extensionInDays: daysBetween(this.currentSoftDeletionDate, this.softDeletionDate),
      maximumExtensionInDays: this.maximumExtensionInDays,
    }
  }

  /**
   * How far the marker may be dragged. Without a cap it stops at the edge of the scale, which is as
   * far as the timeline can show; the field below stays open for a date further out than that.
   */
  private get maximumExtensionInDays(): number {
    if (this.maximumDeletionDate === null) {
      return UNCAPPED_LOOKAHEAD_IN_DAYS
    }
    // Counted in calendar days on the hard-deletion date, because that is the date an extension adds
    // days to and the date the cap bounds. Counting elapsed hours instead would leave the marker a
    // day short of the maximum whenever a daylight-saving change shortens one of those days.
    const candidate = Math.max(0, differenceInDays(this.maximumDeletionDate, this.currentDeletionDate))
    const overshoots = candidate > 0 && addDays(this.currentDeletionDate, candidate) > this.maximumDeletionDate
    return overshoots ? candidate - 1 : candidate
  }

  /**
   * Where the scale ends: at the organization's maximum where it has one, and a month past the
   * current deletion date where it has none — widened if a date beyond that is typed into the field,
   * so the marker stays in view.
   */
  private get windowEndDate(): Date {
    if (this.maximumDeletionDate !== null) {
      return this.withHeadroom(this.maximumDeletionDate)
    }
    const lookahead = addDays(this.currentDeletionDate, UNCAPPED_LOOKAHEAD_IN_DAYS)
    return this.deletionDate > lookahead ? this.withHeadroom(this.deletionDate) : lookahead
  }

  private withHeadroom(date: Date): Date {
    const span = date.getTime() - this.meetingDate.getTime()
    return new Date(date.getTime() + span * SCALE_HEADROOM)
  }

  get dialogResult(): ExtendRetentionDialogResult {
    return { deletionDate: this.deletionDate }
  }
}
